import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PHOTO_ACCEPT, validatePhotoFile } from "../../lib/photoValidation";
import PrivateNewsImage from "../../components/PrivateNewsImage";
import { PageHeader, Card, CardBody, Button, Input, Empty, Textarea } from "../../components/admin/UI";
import { Modal, useModal } from "../../components/admin/Modal";
import { ImagePlus, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { title: "", excerpt: "", content: "", is_published: true };

export default function News() {
    const { activeClubId } = useAuth();
    const [items, setItems] = useState([]);
    const { open, openModal, closeModal } = useModal();
    const [form, setForm] = useState(EMPTY_FORM);
    const [editing, setEditing] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedPreview, setSelectedPreview] = useState("");
    const [removeImage, setRemoveImage] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = () => {
        if (!activeClubId) return;
        api.get("/comms/news", { params: { club_id: activeClubId } }).then((r) => setItems(r.data));
    };
    useEffect(load, [activeClubId]);

    useEffect(() => {
        if (!selectedFile) {
            setSelectedPreview("");
            return undefined;
        }
        const objectUrl = URL.createObjectURL(selectedFile);
        setSelectedPreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [selectedFile]);

    const resetEditor = () => {
        setForm(EMPTY_FORM);
        setEditing(null);
        setSelectedFile(null);
        setRemoveImage(false);
    };

    const startCreate = () => {
        resetEditor();
        openModal();
    };

    const startEdit = (item) => {
        setEditing(item);
        setForm({
            title: item.title || "",
            excerpt: item.excerpt || "",
            content: item.content || "",
            is_published: item.is_published !== false,
        });
        setSelectedFile(null);
        setRemoveImage(false);
        openModal();
    };

    const closeEditor = () => {
        closeModal();
        resetEditor();
    };

    const chooseImage = (event) => {
        const file = event.target.files?.[0] || null;
        const error = validatePhotoFile(file);
        if (error) {
            toast.error(error);
            event.target.value = "";
            return;
        }
        setSelectedFile(file);
        setRemoveImage(false);
    };

    const markImageForRemoval = () => {
        setSelectedFile(null);
        setRemoveImage(true);
    };

    const uploadImage = async (newsId, file) => {
        const body = new FormData();
        body.append("file", file);
        await api.put(`/comms/news/${newsId}/image`, body, {
            params: { club_id: activeClubId },
        });
    };

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        let createdId = null;
        try {
            let newsId = editing?.id;
            if (editing) {
                await api.patch(`/comms/news/${newsId}`, form, { params: { club_id: activeClubId } });
            } else {
                const response = await api.post("/comms/news", form, { params: { club_id: activeClubId } });
                newsId = response.data.id;
                createdId = newsId;
            }

            if (selectedFile) {
                await uploadImage(newsId, selectedFile);
            } else if (editing && removeImage) {
                await api.delete(`/comms/news/${newsId}/image`, { params: { club_id: activeClubId } });
            }

            toast.success(editing ? "Actualité mise à jour" : "Actualité publiée");
            closeEditor();
            load();
        } catch (error) {
            // Avoid creating duplicate text-only articles when an initial image upload fails.
            if (createdId) {
                try {
                    await api.delete(`/comms/news/${createdId}`, { params: { club_id: activeClubId } });
                } catch (_) {
                    // The article remains visible in the refreshed list for an explicit retry/edit.
                    load();
                }
            }
            toast.error(formatApiError(error));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cette actualité ?")) return;
        try {
            await api.delete(`/comms/news/${id}`, { params: { club_id: activeClubId } });
            load();
        } catch (error) {
            toast.error(formatApiError(error));
        }
    };

    const hasCurrentImage = Boolean(editing?.has_image) && !removeImage && !selectedFile;

    return (
        <div data-testid="news-page">
            <PageHeader
                title="Actualités (site public)"
                subtitle="Publiez les news visibles sur le site public du club"
                action={<Button onClick={startCreate} data-testid="add-news-button"><Plus className="w-4 h-4" /> Nouvelle actualité</Button>}
            />

            {items.length === 0 ? (
                <Empty title="Aucune actualité" hint="Publiez votre première news." />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((item) => (
                        <Card key={item.id} className="overflow-hidden">
                            {item.has_uploaded_image && (
                                <PrivateNewsImage newsId={item.id} clubId={activeClubId} alt={item.title} className="w-full h-40 object-cover" />
                            )}
                            {!item.has_uploaded_image && item.image_url && (
                                <img src={item.image_url} alt={item.title} className="w-full h-40 object-cover" />
                            )}
                            <CardBody>
                                <div className="flex items-start justify-between gap-3">
                                    <h3 className="font-display font-bold text-slate-900">{item.title}</h3>
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={() => startEdit(item)} aria-label={`Modifier ${item.title}`} className="text-slate-400 hover:text-orange-500"><Pencil className="w-4 h-4" /></button>
                                        <button onClick={() => remove(item.id)} aria-label={`Supprimer ${item.title}`} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </div>
                                {item.excerpt && <p className="text-sm text-slate-500 mt-1">{item.excerpt}</p>}
                                <div className="text-xs text-slate-400 mt-2">{item.published_at?.slice(0, 10)}</div>
                            </CardBody>
                        </Card>
                    ))}
                </div>
            )}

            <Modal open={open} onClose={closeEditor} title={editing ? "Modifier l'actualité" : "Nouvelle actualité"} size="lg">
                <form onSubmit={submit} className="space-y-4" data-testid="news-form">
                    <Input label="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                    <div>
                        <span className="text-xs font-semibold text-slate-700 mb-1 block uppercase tracking-wider">Image (optionnelle)</span>
                        {(selectedPreview || hasCurrentImage) && (
                            <div className="relative rounded-xl overflow-hidden bg-slate-100 mb-3 h-52">
                                {selectedPreview && <img src={selectedPreview} alt="Aperçu de la sélection" className="w-full h-full object-cover" />}
                                {!selectedPreview && editing?.has_uploaded_image && (
                                    <PrivateNewsImage newsId={editing.id} clubId={activeClubId} alt={editing.title} className="w-full h-full object-cover" />
                                )}
                                {!selectedPreview && !editing?.has_uploaded_image && editing?.image_url && (
                                    <img src={editing.image_url} alt={editing.title} className="w-full h-full object-cover" />
                                )}
                                <button type="button" onClick={markImageForRemoval} aria-label="Supprimer l'image" className="absolute top-2 right-2 rounded-full bg-white/90 p-2 text-red-600 shadow hover:bg-white">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-300">
                            <ImagePlus className="w-4 h-4" />
                            {selectedFile || editing?.has_image ? "Remplacer l'image" : "Ajouter une image"}
                            <input type="file" accept={PHOTO_ACCEPT} onChange={chooseImage} className="sr-only" data-testid="news-image-input" />
                        </label>
                        {removeImage && <p className="text-xs text-slate-500 mt-2">L'image actuelle sera supprimée à l'enregistrement.</p>}
                        <p className="text-xs text-slate-500 mt-2">JPEG, PNG ou WebP, 10 Mo maximum.</p>
                    </div>
                    <Textarea label="Résumé" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
                    <Textarea label="Contenu" rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required />
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" type="button" onClick={closeEditor} disabled={saving}>Annuler</Button>
                        <Button type="submit" data-testid="news-submit" disabled={saving}>{saving ? "Enregistrement..." : editing ? "Enregistrer" : "Publier"}</Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
